import get from 'lodash/get';
import { useMemo } from 'react';

import {
   MedicalLeaveForGuardian,
   MedicalLeaveForGuardianDraftQuery,
   MedicalLeavePayer,
} from '@dreryk/egabinet-ezla-frontend-api';
import { Icd10Entry } from '@dreryk/egabinet-icd-dictionary-published-language-api';

import { useCqrsFindFirst, UseCqrsQueryResult } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';

import { DEFAULT_INSURANCE_TYPE } from '../common/common.constants';
import { LeaveAfterTerminationOfEmploymentRecord } from '../common/common.types';
import { createNewDateRange, mapPatientAddressToMedicalLeaveFormAddress } from '../common/common.utils';
import { getMedicalLeaveFormInitialValues } from '../medicalleave/medicalLeave.constants';

import { MedicalLeaveForGuardianDraft } from './guardianMedicalLeave.types';

const pagination = paginations.first<MedicalLeaveForGuardian | null>();

export const useMedicalLeaveForGuardianDraftQuery = (
   visitId: number
): UseCqrsQueryResult<MedicalLeaveForGuardian | null> => {
   return useCqrsFindFirst<MedicalLeaveForGuardian>(() => {
      const query = new MedicalLeaveForGuardianDraftQuery();
      query.visitId = visitId;
      return query;
   }, pagination);
};

export const findMedicalLeaveDiagnosis = (medicalLeave?: MedicalLeaveForGuardian): Icd10Entry | null =>
   get(medicalLeave, 'medicalLeaveForGuardian.medicalLeave.diagnosis', null);

export const useMedicalLeaveForGuardianDraft = (
   medicalLeaveForGuardian?: MedicalLeaveForGuardian | null
): MedicalLeaveForGuardianDraft | undefined => {
   const medicalLeave = medicalLeaveForGuardian?.medicalLeave;
   const guardianDetails = medicalLeaveForGuardian?.guardianDetails;
   const guardianRequiredDataPresent =
      guardianDetails?.firstName && guardianDetails?.lastName && guardianDetails?.guardianExternalIdentifier;
   const insuranceType = medicalLeave?.insuranceType ?? DEFAULT_INSURANCE_TYPE;
   return useMemo<MedicalLeaveForGuardianDraft | undefined>(() => {
      if (!medicalLeaveForGuardian) {
         return undefined;
      }
      const diagnosis = findMedicalLeaveDiagnosis(medicalLeaveForGuardian ?? undefined);
      const startDate = medicalLeaveForGuardian?.medicalLeave?.dateFrom ?? null;
      const endDate = medicalLeaveForGuardian?.medicalLeave?.dateTo ?? null;
      const { address } = medicalLeave ?? {};
      return {
         ...getMedicalLeaveFormInitialValues(),
         ...(medicalLeave || {}),
         id: medicalLeaveForGuardian?.medicalLeave.id,
         externalId: medicalLeaveForGuardian.medicalLeave.externalId,
         // TODO DRWEB-4260 use Range from backend
         leaveDateRange: createNewDateRange(startDate, endDate),
         diagnosis: diagnosis,
         insuranceType: insuranceType,
         leaveAfterTerminationOfEmployment: (medicalLeave?.payers || []).reduce(
            (acc: LeaveAfterTerminationOfEmploymentRecord, payer: MedicalLeavePayer) => {
               const identificationNumber = payer.identificationNumber?.identificationNumber;
               if (!identificationNumber) {
                  return acc;
               }

               return {
                  ...acc,
                  [identificationNumber]: Boolean(payer.afterTerminationOfEmployment),
               };
            },
            {}
         ),
         guardian: guardianRequiredDataPresent
            ? {
                 firstName: guardianDetails.firstName!.asString(),
                 middleName: guardianDetails.middleName?.asString() ?? null,
                 lastName: guardianDetails.lastName!.asString(),
                 guardianExternalIdentifier: guardianDetails.guardianExternalIdentifier!,
                 address: address ? mapPatientAddressToMedicalLeaveFormAddress(address) : null,
                 relationCode: guardianDetails.relationCode,
                 birthDate: guardianDetails.birthDate ?? null,
              }
            : null,
         relationCode: guardianDetails?.relationCode ?? 'CHILD',
         payers: medicalLeave?.payers ?? [],
         address: address ? mapPatientAddressToMedicalLeaveFormAddress(address) : null,
         birthDate: guardianDetails?.birthDate ?? null,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [medicalLeaveForGuardian]);
};
